import { Injectable } from '@angular/core';
import { from, Observable, map } from 'rxjs';
import { ApiService } from 'src/app/services/api.service';
import { IUserEntity } from 'src/app/core/model/server/vo/IUserEntity.interface';
import * as vo from 'src/app/core/model/server/vo/_index';
import { AuthService } from 'src/app/core/services/auth.service';

@Injectable({ providedIn: 'root' })
export class AdminPortalAccountService {

  constructor(
    private api: ApiService, 
    private authService: AuthService,
  ) {}


  /**
   * Fetches a paginated, filtered list of portal users.
   * Maps the raw API response into a flat shape the component can bind directly.
   */
  getUsers(
    page: number,
    size: number,
    request: vo.GetUserRequest
  ): Observable<{ rows: IUserEntity[]; totalItems: number; totalPages: number }> {
    return this.api.getUsersAll(page, size, request).pipe(
      map((res: any) => {
        const allRows: IUserEntity[] = res.result ?? [];
        const totalItems = allRows.length;
        const totalPages = Math.ceil(totalItems / size) || 1;

        const start = (page - 1) * size;
        const rows  = allRows.slice(start, start + size);

        return { rows, totalItems, totalPages };
      })
    );
}

  /**
   * Creates a new portal user account.
   */
  createUser(request: vo.CreateUserRequest): Observable<vo.CreateUserResponse> {
    return this.api.createUser(request);
  }

  /**
   * Update a portal user account.
   */
  updateUser(uid: number, request: vo.UpdateUserRequest): Observable<vo.UpdateUserResponse> {
    return this.api.updateUser(uid, request);
  }


  /**
   * Enables or disables a portal user account.
   * Only admins / super-admins may do this (enforced server-side too).
   */
  toggleUserStatus(uid: number, status: number): Observable<vo.UpdateUserResponse> {
    return this.api.updateUser(uid, { status });
  }

  /**
   * Returns a human-readable status label.
   */
  getStatusLabel(status: number): string {
    return status === 0 ? 'Active' : 'Inactive';
  }

  /**
   * Returns whether the given status value means "enabled".
   */
  isEnabled(status: number): boolean {
    return status === 0;
  }

  get isAdmin(): boolean {
    return this.authService.isAdmin();
  }


  get currentUserUid(): number {
    return this.authService.getUserUID();
  }

}
